import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Search } from '@carbon/react';
import classNames from 'classnames';
import type { ColorByOption } from 'components/color-by-attribute-modal';
import {
  WELL_FILTER_CATEGORIES,
  wellFilterLabel,
  type WellFilters,
} from './well-filters';
import styles from './area-well-filters-modal.module.scss';

interface AreaWellFiltersModalProps {
  open: boolean;
  /** The filters currently applied, seeded into the working copy on open. */
  filters: WellFilters;
  /** The attribute to open on — an applied chip's edit path. */
  initialCategory?: string | null;
  /** Value lists, from the same facet call the colour modal uses. */
  getOptions: (categoryId: string) => ColorByOption[];
  /** True while the facet call is in flight. */
  loading: boolean;
  /** Why the values couldn't be loaded, or null. */
  error?: string | null;
  onClose: () => void;
  onApply: (filters: WellFilters) => void;
}

// Picks which wells the table and map show, by the colour-by attributes. Two columns as
// Type Curve has them; categorical values only, no numeric ranges or date quarters.
export const AreaWellFiltersModal = ({
  open,
  filters,
  initialCategory,
  getOptions,
  loading,
  error,
  onClose,
  onApply,
}: AreaWellFiltersModalProps): JSX.Element => {
  const [pending, setPending] = useState<WellFilters>({});
  const [activeCategory, setActiveCategory] = useState(
    WELL_FILTER_CATEGORIES[0].id
  );
  const [search, setSearch] = useState('');

  // Seed the working copy from what is applied, and open on the asked-for
  // attribute, only when the modal opens — re-seeding mid-edit would discard work.
  useEffect(() => {
    if (!open) {
      return;
    }
    setPending({ ...filters });
    setActiveCategory(initialCategory ?? WELL_FILTER_CATEGORIES[0].id);
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(
    () => getOptions(activeCategory),
    [getOptions, activeCategory]
  );
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, search]);

  const selected = pending[activeCategory] ?? [];

  // Adds or removes one value from the attribute being edited.
  const toggleValue = (id: string) =>
    setPending((current) => {
      const values = current[activeCategory] ?? [];
      const next = values.includes(id)
        ? values.filter((value) => value !== id)
        : [...values, id];
      return { ...current, [activeCategory]: next };
    });

  return (
    <Modal
      open={open}
      modalHeading="Add Filter"
      primaryButtonText="Apply"
      secondaryButtonText="Cancel"
      onRequestSubmit={() => onApply(pending)}
      onRequestClose={onClose}
      size="lg"
      className={styles.modal}
    >
      <div className={styles.columns}>
        <ul className={styles.categories} aria-label="Attributes">
          {WELL_FILTER_CATEGORIES.map((category) => {
            const count = (pending[category.id] ?? []).length;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  className={classNames(styles.category, {
                    [styles.categoryActive]: category.id === activeCategory,
                  })}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setSearch('');
                  }}
                >
                  {category.label}
                  {count > 0 && (
                    <span className={styles.categoryCount}>{count}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <div className={styles.values}>
          <p className={styles.valuesTitle}>
            {wellFilterLabel(activeCategory)}
          </p>

          <Search
            size="lg"
            labelText={`Search ${wellFilterLabel(activeCategory)}`}
            placeholder={`Search ${wellFilterLabel(
              activeCategory
            ).toLowerCase()}…`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <div className={styles.valueActions}>
            <Button
              kind="ghost"
              size="sm"
              disabled={visible.length === 0}
              onClick={() =>
                setPending((current) => ({
                  ...current,
                  [activeCategory]: visible.map((option) => option.id),
                }))
              }
            >
              Select All
            </Button>
            <Button
              kind="ghost"
              size="sm"
              disabled={selected.length === 0}
              onClick={() =>
                setPending((current) => ({ ...current, [activeCategory]: [] }))
              }
            >
              Clear
            </Button>
          </div>

          {/* Only while there is nothing to show: the modal re-reads the values on open,
              and saying "loading" over a full list already on screen reads as a stall. */}
          {loading && visible.length === 0 && (
            <p className={styles.note}>Loading values…</p>
          )}
          {!loading && error && <p className={styles.error}>{error}</p>}
          {!loading && !error && visible.length === 0 && (
            <p className={styles.note}>No values to filter on.</p>
          )}

          {visible.map((option) => (
            <Checkbox
              key={option.id}
              id={`area-well-filter-${activeCategory}-${option.id}`}
              labelText={option.name}
              checked={selected.includes(option.id)}
              onChange={() => toggleValue(option.id)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};
